import { _ as _class_call_check } from "@swc/helpers/_/_class_call_check";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { isNumberAutoScaleFormatAutoUnit, parseNumberAutoScaleFormatAutoUnit } from '../../api/NumberAutoScaleFormatApi.js';
var UNIT_GROUPS = new Map();
var UNIT_TO_GROUP_NAME = new Map();
var isNumberAutoScaleFormatOptions = function isNumberAutoScaleFormatOptions(options) {
    return !!options && options.style === 'unit' && isNumberAutoScaleFormatAutoUnit(options.unit);
};
export var createNumberFormat = function createNumberFormat(locales, options) {
    return isNumberAutoScaleFormatOptions(options) ? new NumberAutoScaleFormat(locales, options) : new Intl.NumberFormat(locales, options);
};
export var NumberAutoScaleFormat = /*#__PURE__*/ function() {
    "use strict";
    function NumberAutoScaleFormat(locales, options) {
        _class_call_check(this, NumberAutoScaleFormat);
        var _UNIT_TO_GROUP_NAME_get;
        // Even though the TypeScript signature forces passing the correct options, validate.
        if (!isNumberAutoScaleFormatOptions(options)) {
            throw new TypeError('NumberAutoScaleFormat requires options that have style: "unit" and a valid auto-scale unit in the form of "auto-<auto_unit>(-per-<unit>)"');
        }
        var match = parseNumberAutoScaleFormatAutoUnit(options.unit);
        if (!match) {
            throw new RangeError("Unit ".concat(options.unit, " is not supported for auto-scale"));
        }
        var scales = UNIT_GROUPS.get((_UNIT_TO_GROUP_NAME_get = UNIT_TO_GROUP_NAME.get(match[1])) !== null && _UNIT_TO_GROUP_NAME_get !== void 0 ? _UNIT_TO_GROUP_NAME_get : '');
        // Note: the else branch throws, but using the if branch to scope the i variable too.
        if (scales) {
            var i = scales.findIndex(function(scale) {
                return scale.name === match[1];
            });
            if (i > 0) {
                scales = Object.freeze(scales.slice(i));
            }
        } else {
            throw new RangeError("Unit ".concat(match[1], "} is not supported for auto-scale"));
        }
        var intlOptions = _object_spread_props(_object_spread({}, options), {
            unit: scales[0].unit + match[2]
        });
        var cache = new Array(Math.max(scales.length, 1)).fill(null);
        cache[0] = new Intl.NumberFormat(locales, intlOptions);
        var select = function select(startValue, endValue) {
            /*
             * Have to lie about the type since TypeScript doesn't understand that it's truly EITHER bigint or number,
             * with all values being of the same (single) type. And thus it will complain that we can't multiply
             * `bigint | number` with `bigint | number` on any operation. To work around this, coerce the type to "bigint",
             * because it is more restricted. This way, TypeScript checks no operations are used that do not work with
             * bigint. (For example, none of the Math methods, such as Math.abs, accept bigint values).
             */ var normalize = (typeof startValue === "undefined" ? "undefined" : _type_of(startValue)) === 'bigint' ? BigInt : Number;
            // Always use start value. For ranges, startValue must be <= endValue, so no need to search the minimum.
            // Also, always use the same scale for both values, since comparing two different scales is confusing.
            var value = normalize(startValue);
            var i = 0;
            // Note: this purposefully scales factor, which can only be integer multiplication, and then does a single division.
            // This lowers the effect of float rounding errors compared to repeated divisions of startValue and endValue.
            var factor = normalize(1);
            if ((typeof value === "undefined" ? "undefined" : _type_of(value)) === 'bigint' || !Number.isNaN(value) && Number.isFinite(value)) {
                var valueAbs = value < normalize(0) ? -value : value;
                while(i + 1 < scales.length && valueAbs >= factor * normalize(scales[i + 1].deltaFactor)){
                    ++i;
                    factor *= normalize(scales[i].deltaFactor);
                }
            }
            var fmt = cache[i];
            if (!fmt) {
                intlOptions.unit = scales[i].unit + match[2];
                fmt = new Intl.NumberFormat(locales, intlOptions);
                cache[i] = fmt;
            }
            return endValue === undefined ? [
                fmt,
                value / factor
            ] : [
                fmt,
                value / factor,
                normalize(endValue) / factor
            ];
        };
        Object.defineProperties(this, {
            select: {
                value: select
            },
            unit: {
                value: match[0].toLowerCase()
            }
        });
    }
    var _proto = NumberAutoScaleFormat.prototype;
    _proto.resolvedOptions = function resolvedOptions() {
        return _object_spread_props(_object_spread({}, this.select(NaN)[0].resolvedOptions()), {
            unit: this.unit
        });
    };
    _proto.format = function format(value) {
        var _this_select = _sliced_to_array(this.select(value), 2), fmt = _this_select[0], v = _this_select[1];
        return fmt.format(v);
    };
    _proto.formatToParts = function formatToParts(value) {
        var _this_select = _sliced_to_array(this.select(value), 2), fmt = _this_select[0], v = _this_select[1];
        return fmt.formatToParts(v);
    };
    _proto.formatRange = function formatRange(start, end) {
        var _this_select = _sliced_to_array(this.select(start, end), 3), fmt = _this_select[0], s = _this_select[1], e = _this_select[2];
        return fmt.formatRange(s, e);
    };
    _proto.formatRangeToParts = function formatRangeToParts(start, end) {
        var _this_select = _sliced_to_array(this.select(start, end), 3), fmt = _this_select[0], s = _this_select[1], e = _this_select[2];
        return fmt.formatRangeToParts(s, e);
    };
    return NumberAutoScaleFormat;
}();
// Unit group definitions
var define = function define(name, deltaFactor) {
    var unit = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : name;
    if (!Number.isInteger(deltaFactor)) {
        throw new RangeError('Only integer factors are supported.');
    }
    return Object.freeze({
        name: name,
        unit: unit,
        deltaFactor: deltaFactor
    });
};
var add = function add(map, key, value) {
    if (map.has(key)) {
        throw new Error("Duplicate key ".concat(key));
    }
    map.set(key, value);
};
add(UNIT_GROUPS, 'bit', Object.freeze([
    define('bit', 1),
    define('kilobit', 1000),
    define('megabit', 1000),
    define('gigabit', 1000),
    define('terabit', 1000)
]));
add(UNIT_GROUPS, 'byte', Object.freeze([
    define('byte', 1),
    define('kilobyte', 1000),
    define('megabyte', 1000),
    define('gigabyte', 1000),
    define('terabyte', 1000),
    define('petabyte', 1000)
]));
/*
 * This is using the powers of 1024 scale, while still using the SI prefixes.
 * Intl doesn't (yet) support Ki/Mi/Gi/Ti/Pi units, and it's even still common practice to use
 * the k/M/G/T/P prefixes with power of 1024 scale in certain contexts (RAM memory size).
 * Windows 11 also still displays file sizes using powers of 1024 but with the "wrong" prefixes.
 */ add(UNIT_GROUPS, 'binary-byte', Object.freeze([
    define('bibyte', 1, 'byte'),
    define('kibibyte', 1024, 'kilobyte'),
    define('mebibyte', 1024, 'megabyte'),
    define('gibibyte', 1024, 'gigabyte'),
    define('tebibyte', 1024, 'terabyte'),
    define('pebibyte', 1024, 'petabyte')
]));
add(UNIT_GROUPS, 'weight-metric', Object.freeze([
    define('gram', 1),
    define('kilogram', 1000)
]));
add(UNIT_GROUPS, 'weight-imperial', Object.freeze([
    define('ounce', 1),
    define('pound', 16),
    define('stone', 14)
]));
add(UNIT_GROUPS, 'volume-metric', Object.freeze([
    define('milliliter', 1),
    define('liter', 1000)
]));
add(UNIT_GROUPS, 'length-metric', Object.freeze([
    define('millimeter', 1),
    define('centimeter', 10),
    define('meter', 100),
    define('kilometer', 1000)
]));
add(UNIT_GROUPS, 'length-imperial', Object.freeze([
    define('inch', 1),
    define('foot', 12),
    define('yard', 3),
    define('mile', 1760)
]));
add(UNIT_GROUPS, 'time', Object.freeze([
    define('millisecond', 1),
    define('second', 1000),
    define('minute', 60),
    define('hour', 60),
    define('day', 24),
    define('week', 7)
]));
UNIT_GROUPS.forEach(function(units, scaleGroup) {
    return units.forEach(function(param) {
        var name = param.name;
        return add(UNIT_TO_GROUP_NAME, name, scaleGroup);
    });
});

//# sourceMappingURL=NumberAutoScaleFormat.js.map